import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, TreePine, Users, Calendar, ArrowRight } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Breadcrumbs } from '../components/layout/Breadcrumbs';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const counties = [
  {
    name: 'Rutland',
    description: 'England\'s smallest county, home to Rutland Water and rolling hills.',
    image: '/images/cpre images for website/17.jpg',
    mapBbox: '-0.8,52.55,-0.5,52.8',
    stats: { projects: 12, volunteers: 85, events: 6 }
  },
  {
    name: 'Lincolnshire',
    description: 'From the Wolds to the Fens, a county of diverse landscapes.',
    image: '/images/cpre images for website/13.jpg',
    mapBbox: '-0.8,52.8,0.3,53.6',
    stats: { projects: 18, volunteers: 120, events: 8 }
  },
];

const categories = [
  { id: 'habitat', name: 'Habitat Restoration', icon: TreePine, color: 'bg-cpre-new-yew' },
  { id: 'trails', name: 'Access & Trails', icon: MapPin, color: 'bg-cpre-autumn-leaf' },
  { id: 'farming', name: 'Farming Partnerships', icon: Users, color: 'bg-cpre-hill' },
];

const InitiativesPage = () => {
  const [stats, setStats] = useState({ projects_count: 25, volunteers_count: 150, events_count: 12 });
  const [selectedCounty, setSelectedCounty] = useState(null);
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'map'

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/statistics`);
        setStats(response.data);
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      }
    };
    fetchStats();
  }, []);

  const activeCounty = selectedCounty ? counties.find(c => c.name === selectedCounty) : null;

  return (
    <Layout>
      <div data-testid="initiatives-page">
        {/* Hero */}
        <section className="relative py-20 md:py-28 overflow-hidden bg-cpre-new-yew">
          <div className="absolute inset-0 opacity-20">
            <img
              src="/images/cpre images for website/11.jpg"
              alt="Countryside"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="relative container-custom">
            <Breadcrumbs variant="light" customItems={[{ label: 'Countryside Initiatives' }]} />
            <div className="text-center text-white mt-8">
              <Badge className="bg-cpre-harvest-gold text-cpre-new-yew hover:bg-cpre-autumn-leaf hover:text-white mb-4">Rutland & Lincolnshire</Badge>
              <h1 className="text-4xl sm:text-5xl font-heading font-bold mb-4 text-white">Countryside Initiatives</h1>
              <p className="text-xl text-white/90 max-w-2xl mx-auto">
                Discover our conservation efforts across Rutland and Lincolnshire.
              </p>
            </div>
          </div>
        </section>

        {/* Statistics */}
        <section className="py-12 bg-white border-b border-stone-200">
          <div className="container-custom">
            <div className="grid grid-cols-3 gap-8 max-w-3xl mx-auto text-center">
              <div>
                <span className="block text-4xl font-heading font-bold text-cpre-new-yew">{stats.projects_count}+</span>
                <span className="text-muted-foreground">Active Projects</span>
              </div>
              <div>
                <span className="block text-4xl font-heading font-bold text-cpre-autumn-leaf">{stats.volunteers_count}+</span>
                <span className="text-muted-foreground">Volunteers</span>
              </div>
              <div>
                <span className="block text-4xl font-heading font-bold text-cpre-hill">{stats.events_count}+</span>
                <span className="text-muted-foreground">Events This Year</span>
              </div>
            </div>
          </div>
        </section>

        {/* County Switcher */}
        <section className="section-padding">
          <div className="container-custom">
            <div className="text-center mb-8">
              <span className="text-cpre-autumn-leaf font-medium text-sm uppercase tracking-wider">Explore by Region</span>
              <h2 className="mt-2">Our Counties</h2>
            </div>

            {/* County Switcher Tabs */}
            <div className="flex justify-center gap-4 mb-8">
              <Button
                variant={selectedCounty === null ? "default" : "outline"}
                onClick={() => setSelectedCounty(null)}
                className={selectedCounty === null 
                  ? "bg-cpre-new-yew hover:bg-cpre-hill text-white rounded-full px-6" 
                  : "border-cpre-new-yew text-cpre-new-yew hover:bg-cpre-new-yew hover:text-white rounded-full px-6"
                }
                data-testid="county-all"
              >
                All Regions
              </Button>
              {counties.map((county) => (
                <Button
                  key={county.name}
                  variant={selectedCounty === county.name ? "default" : "outline"}
                  onClick={() => setSelectedCounty(county.name)}
                  className={selectedCounty === county.name 
                    ? "bg-cpre-new-yew hover:bg-cpre-hill text-white rounded-full px-6" 
                    : "border-cpre-new-yew text-cpre-new-yew hover:bg-cpre-new-yew hover:text-white rounded-full px-6"
                  }
                  data-testid={`county-switch-${county.name.toLowerCase()}`}
                >
                  {county.name}
                </Button>
              ))}
            </div>

            {/* View Mode Toggle */}
            <div className="flex justify-center gap-2 mb-8">
              <div className="flex gap-2 bg-stone-100 rounded-lg p-1">
                <Button
                  variant={viewMode === 'cards' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('cards')}
                  className={viewMode === 'cards' ? 'bg-cpre-autumn-leaf text-white shadow-sm hover:bg-cpre-autumn-leaf/90' : 'text-countryside-green hover:bg-countryside-green hover:text-white'}
                >
                  Card View
                </Button>
                <Button
                  variant={viewMode === 'map' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('map')}
                  className={viewMode === 'map' ? 'bg-cpre-autumn-leaf text-white shadow-sm hover:bg-cpre-autumn-leaf/90' : 'text-countryside-green hover:bg-countryside-green hover:text-white'}
                >
                  Map View
                </Button>
              </div>
            </div>

            {/* Map View */}
            {viewMode === 'map' && (
              <div className="mb-12 max-w-4xl mx-auto">
                <div className="aspect-video rounded-2xl overflow-hidden border border-stone-200 shadow-lg">
                  <iframe
                    title="County Map"
                    width="100%"
                    height="100%"
                    frameBorder="0"
                    scrolling="no"
                    src={`https://www.openstreetmap.org/export/embed.html?bbox=${
                      activeCounty ? activeCounty.mapBbox : '-1.5,52.0,0.5,53.5'
                    }&layer=mapnik`}
                  />
                </div>
                <p className="text-center text-sm text-muted-foreground mt-4">
                  {activeCounty 
                    ? `Viewing ${activeCounty.name} - ${activeCounty.stats.projects} projects, ${activeCounty.stats.volunteers} volunteers`
                    : 'Our initiatives span across Rutland and Lincolnshire, from the Wolds to the Fens.'
                  }
                </p>
              </div>
            )}

            {/* County Cards */}
            {viewMode === 'cards' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                {counties
                  .filter(county => !selectedCounty || county.name === selectedCounty)
                  .map((county, index) => (
                  <Link 
                    key={county.name} 
                    to={`/county/${county.name.toLowerCase()}`}
                  >
                    <Card 
                      className="bg-white border-stone-200 overflow-hidden group hover:shadow-lg transition-shadow duration-300 h-full"
                      data-testid={`county-card-${index}`}
                    >
                      <div className="aspect-video overflow-hidden relative">
                        <img
                          src={county.image}
                          alt={county.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                        <h3 className="absolute bottom-4 left-4 text-white text-2xl font-heading font-bold">
                          {county.name}
                        </h3>
                      </div>
                      <CardContent className="p-6">
                        <p className="text-muted-foreground mb-4">{county.description}</p>
                        
                        {/* County Stats */}
                        <div className="grid grid-cols-3 gap-4 mb-4 py-4 border-t border-b border-stone-100">
                          <div className="text-center">
                            <span className="block text-lg font-heading font-bold text-cpre-new-yew">{county.stats.projects}</span>
                            <span className="text-xs text-muted-foreground">Projects</span>
                          </div>
                          <div className="text-center">
                            <span className="block text-lg font-heading font-bold text-cpre-autumn-leaf">{county.stats.volunteers}</span>
                            <span className="text-xs text-muted-foreground">Volunteers</span>
                          </div>
                          <div className="text-center">
                            <span className="block text-lg font-heading font-bold text-cpre-hill">{county.stats.events}</span>
                            <span className="text-xs text-muted-foreground">Events</span>
                          </div>
                        </div>

                        <span className="inline-flex items-center text-cpre-new-yew font-medium text-sm group-hover:gap-3 gap-2 transition-all duration-200">
                          Explore {county.name}
                          <ArrowRight className="w-4 h-4" />
                        </span>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Initiative Categories */}
        <section className="section-padding bg-cpre-pebble/30">
          <div className="container-custom">
            <div className="text-center mb-12">
              <span className="text-cpre-autumn-leaf font-medium text-sm uppercase tracking-wider">Focus Areas</span>
              <h2 className="mt-2">Initiative Categories</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {categories.map((category, index) => (
                <Link key={category.id} to={`/projects?category=${category.id}`}>
                  <Card 
                    className="bg-white border-stone-200 hover:shadow-lg transition-shadow duration-300 h-full group"
                    data-testid={`category-card-${index}`}
                  >
                    <CardContent className="p-8 text-center">
                      <div className={`w-16 h-16 ${category.color} rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300`}>
                        <category.icon className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="font-heading font-semibold text-xl mb-3">{category.name}</h3>
                      <p className="text-muted-foreground text-sm">
                        {category.id === 'habitat' && 'Restoring and protecting natural habitats for wildlife across the region.'}
                        {category.id === 'trails' && 'Maintaining and improving public access to the countryside.'}
                        {category.id === 'farming' && 'Working with farmers to implement sustainable practices.'}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Resources */}
        <section className="section-padding bg-cpre-new-yew text-white">
          <div className="container-custom text-center">
            <h2 className="text-3xl font-heading font-bold mb-6 text-white">Get Involved</h2>
            <p className="text-xl text-white/90 max-w-2xl mx-auto mb-8">
              Discover how you can support our countryside initiatives.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/volunteering">
                <Button size="lg" className="bg-white text-cpre-new-yew hover:bg-cpre-mint rounded-full px-8" data-testid="volunteer-link">
                  <Users className="w-5 h-5 mr-2" />
                  Volunteer
                </Button>
              </Link>
              <Link to="/donate">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-cpre-new-yew rounded-full px-8" data-testid="donate-link">
                  Support Our Work
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default InitiativesPage;
